import { validateArenaDef, type ArenaDef, type TilesetDef } from '@archon3d/core';
import { newId } from './newId';

/**
 * The stamps and colours of a tileset that carries none of its own: the tileset's data says them
 * now (`TilesetDef.stamps`, `TileDef.editor`), so a tile added to one is painted in its colour
 * and named, with no release (editor:quality#16).
 */
export const STAMPS: Record<string, string[][]> = {
  Castle: [
    ['castle', 'rock', 'castle'],
    ['rock', 'grass', 'rock'],
    ['castle', 'grass', 'castle'],
  ],
  Tower: [
    ['castle', 'castle'],
    ['castle', 'castle'],
  ],
  Gate: [
    ['rock', 'grass', 'rock'],
    ['rock', 'grass', 'rock'],
  ],
  Pond: [
    ['grass', 'water', 'grass'],
    ['water', 'water', 'water'],
    ['grass', 'water', 'grass'],
  ],
};
export const TILE_COLORS: Record<string, string> = {
  grass: '#547144',
  road: '#b39770',
  swamp: '#586b38',
  ice: '#b0dce2',
  lava: '#e96b37',
  water: '#398bbc',
  rock: '#787879',
  tree: '#214e30',
  castle: '#b5aca0',
  'lamp-wall': '#d6ba6a',
  sand: '#d9c28a',
};

/** The stamps a tileset's editor offers, by name. */
export function stampsOf(tileset: TilesetDef): Record<string, string[][]> {
  return tileset.stamps ? Object.fromEntries(tileset.stamps.map((s) => [s.name, s.cells])) : STAMPS;
}

/** The colour a tile is painted in the editor. */
export function tileColor(tileset: TilesetDef, id: string): string {
  return tileset.tiles.find((t) => t.id === id)?.editor?.color ?? TILE_COLORS[id] ?? '#666';
}

/** The name on a tile's brush. */
export function tileLabel(tileset: TilesetDef, id: string): string {
  return tileset.tiles.find((t) => t.id === id)?.editor?.label ?? id;
}
export function blankArena(tileset: TilesetDef): ArenaDef {
  return {
    id: `arena-${newId()}`,
    name: 'New arena',
    width: 16,
    height: 16,
    tileset: tileset.id,
    legend: { '.': 'grass' },
    rows: Array.from({ length: 16 }, () => '.'.repeat(16)),
    spawns: [
      [2, 8],
      [13, 8],
    ],
    camera: { mode: 'fit' },
  };
}
export function rotateStamp(stamp: string[][], turns: number): string[][] {
  let result = stamp;
  for (let i = 0; i < turns % 4; i++)
    result = result[0]!.map((_, x) => result.map((row) => row[x]!).reverse());
  return result;
}
/** An entire stamp is atomic: invalid bounds or a blocked spawn leaves the arena untouched. */
export function paintArena(
  arena: ArenaDef,
  tileset: TilesetDef,
  x: number,
  y: number,
  stamp: string[][],
): ArenaDef {
  const next = structuredClone(arena);
  const isGroup = stamp.length > 1 || stamp[0]!.length > 1;
  const hit = arena.groups?.find(
    (g) => x >= g.x && y >= g.y && x < g.x + g.width && y < g.y + g.height,
  );
  if (!isGroup && hit) {
    if (stamp[0]![0] !== 'grass') throw new Error('Erase the group first to edit these squares.');
    const cleared = { ...arena, groups: arena.groups!.filter((g) => g !== hit) };
    let result: ArenaDef = structuredClone(cleared);
    for (let cy = hit.y; cy < hit.y + hit.height; cy++)
      for (let cx = hit.x; cx < hit.x + hit.width; cx++)
        result = paintArena(result, tileset, cx, cy, [['grass']]);
    return result;
  }
  for (let dy = 0; dy < stamp.length; dy++)
    for (let dx = 0; dx < stamp[dy]!.length; dx++) {
      const px = x + dx,
        py = y + dy,
        tile = stamp[dy]![dx]!;
      if (px < 0 || py < 0 || px >= arena.width || py >= arena.height)
        throw new Error('The whole block group must fit inside the arena.');
      if (
        isGroup &&
        (arena.groups?.some(
          (g) => px >= g.x && py >= g.y && px < g.x + g.width && py < g.y + g.height,
        ) ||
          arena.legend[arena.rows[py]![px]!] !== 'grass')
      )
        throw new Error('This group overlaps existing terrain or a group. Erase it first.');
      let key = Object.keys(next.legend).find((k) => next.legend[k] === tile);
      if (!key) {
        key = 'abcdefghijklmnopqrstuvwxyz0123456789'.split('').find((k) => !next.legend[k]);
        if (!key) throw new Error('Too many tile types.');
        next.legend[key] = tile;
      }
      next.rows[py] = next.rows[py]!.slice(0, px) + key + next.rows[py]!.slice(px + 1);
    }
  if (isGroup)
    next.groups = [...(next.groups ?? []), { x, y, width: stamp[0]!.length, height: stamp.length }];
  return validateArenaDef(next, { [tileset.id]: tileset });
}
export function resizeArena(
  arena: ArenaDef,
  tileset: TilesetDef,
  width: number,
  height: number,
): ArenaDef {
  if (![width, height].every((n) => Number.isInteger(n) && n >= 8 && n <= 32))
    throw new Error('Arena dimensions must be 8–32 squares.');
  let next = structuredClone(arena);
  const key = Object.keys(next.legend).find((k) => next.legend[k] === 'grass') ?? '.';
  next.legend[key] = 'grass';
  next = {
    ...next,
    width,
    height,
    groups: next.groups?.filter((g) => g.x + g.width <= width && g.y + g.height <= height),
    rows: Array.from({ length: height }, (_, y) =>
      Array.from({ length: width }, (_, x) => arena.rows[y]?.[x] ?? key).join(''),
    ),
  };
  next.spawns = next.spawns.map(([x, y]) => [Math.min(x, width - 1), Math.min(y, height - 1)]);
  if (next.spawns[0]![0] === next.spawns[1]![0] && next.spawns[0]![1] === next.spawns[1]![1])
    next.spawns[1] = [1, 1];
  for (const [x, y] of next.spawns)
    next.rows[y] = next.rows[y]!.slice(0, x) + key + next.rows[y]!.slice(x + 1);
  return validateArenaDef(next, { [tileset.id]: tileset });
}
